// Same glyphs the recipe header strip's edit toggle used, before it moved here.
const ICON_ENTER_EDIT = '✎';
const ICON_EXIT_EDIT = '⎋';

export type NavRoute = 'home' | 'ingredients' | 'transformations' | 'tools' | 'tags';

interface NavLinkDef {
  route: NavRoute;
  label: string;
  path: string;
}

const NAV_LINKS: NavLinkDef[] = [
  { route: 'home', label: 'Receptari', path: '/' },
  { route: 'ingredients', label: 'Ingredients', path: '/ingredients' },
  { route: 'tags', label: 'Etiquetes', path: '/tags' },
  { route: 'transformations', label: 'Transformacions', path: '/transformacions' },
  { route: 'tools', label: 'Utensilis', path: '/utensilis' },
];

export interface SiteNavOptions {
  activeRoute: NavRoute;
  onNavigate: (path: string) => void;
  /** Hides the Transformacions, Utensilis, and Etiquetes links when
   *  false — unlike Receptari/Ingredients, Transformacions/Utensilis
   *  require being logged in to view at all (main.ts's dispatchRoute()
   *  enforces the actual block; this just avoids advertising a link a
   *  logged-out visitor would be turned away from). Etiquetes itself
   *  stays reachable by direct link even logged out — only its nav link
   *  is hidden, for consistency with the other two. */
  isAuthenticated: boolean;
  /** Only rendered when logged in — editing is gated behind login, same
   *  as everything else it controls (recipe editing, the Explorer). */
  editMode?: {
    active: boolean;
    onToggle: () => void;
  };
}

/**
 * Secondary nav bar, rendered directly below the top bar on every page —
 * always visible; browsing the explorer is public, editing it is gated
 * behind login (same pattern as recipe pages).
 *
 * The link row itself is stateless by design: main.ts's dispatchRoute()
 * clears and rebuilds the whole page (including the top bar) on every
 * navigation already, so this just needs to render the *current* active
 * route correctly each time. The edit-mode toggle is the single control
 * for edit mode everywhere (the recipe page no longer has its own — see
 * RecipeHeaderStrip.ts) but its state can still change without a
 * navigation, e.g. an unsaved-changes dialog resolving on the recipe page
 * — main.ts re-invokes this function in place (not a full dispatchRoute())
 * whenever that happens, so it always reflects the current state without
 * losing anything else on the page.
 *
 * Wide screens: inline links. Narrow screens: a hamburger-toggled menu,
 * same `lg:hidden` breakpoint convention TabSelector uses elsewhere. The
 * edit-mode toggle sits at the right on both, via justify-between.
 */
export function renderSiteNav(container: HTMLElement, options: SiteNavOptions): void {
  const { activeRoute, onNavigate, isAuthenticated, editMode } = options;

  container.innerHTML = '';

  const visibleLinks = NAV_LINKS.filter(def =>
    (def.route !== 'transformations' && def.route !== 'tools' && def.route !== 'tags') || isAuthenticated
  );

  const nav = document.createElement('nav');
  nav.className = 'border-b border-[var(--accent-alpha-20)] px-4 py-2';

  const linkClass = (route: NavRoute): string => {
    const base = 'px-2 py-1 text-sm font-mono rounded transition-colors';
    return route === activeRoute
      ? `${base} text-[var(--accent)] bg-[var(--accent-alpha-08)]`
      : `${base} text-[#a0a0a0] hover:text-[var(--accent)]`;
  };

  const makeLink = (def: NavLinkDef): HTMLAnchorElement => {
    const a = document.createElement('a');
    a.href = def.path;
    a.className = linkClass(def.route);
    a.textContent = def.label;
    a.addEventListener('click', (e) => {
      e.preventDefault();
      onNavigate(def.path);
    });
    return a;
  };

  const topRow = document.createElement('div');
  topRow.className = 'flex items-center justify-between gap-2';

  // Wide screens: inline row of links.
  const wideRow = document.createElement('div');
  wideRow.className = 'hidden lg:flex items-center gap-1';
  for (const def of visibleLinks) {
    wideRow.appendChild(makeLink(def));
  }
  topRow.appendChild(wideRow);

  // Narrow screens: hamburger toggle, opening the menu appended below.
  const hamburgerBtn = document.createElement('button');
  hamburgerBtn.type = 'button';
  hamburgerBtn.className = 'lg:hidden px-2 py-1 text-sm font-mono text-[var(--accent)] flex items-center gap-2';
  const activeLabel = visibleLinks.find(l => l.route === activeRoute)?.label ?? '';
  // '≡' (Mathematical Operators), not '☰' (Miscellaneous Symbols) — the
  // latter is missing from enough system/monospace font fallback chains
  // (notably on Firefox/Windows) to render as nothing at all; '≡' reads
  // identically as a hamburger glyph and is covered far more reliably.
  hamburgerBtn.textContent = `≡ ${activeLabel}`;
  topRow.appendChild(hamburgerBtn);

  // Right side: edit-mode toggle (logged in only) — same circular
  // icon-button look the recipe header strip's own edit toggle used to
  // have, now that this replaces it as the single edit-mode control.
  if (editMode) {
    const editBtn = document.createElement('button');
    editBtn.type = 'button';
    editBtn.title = editMode.active ? 'Sortir d\'edició' : 'Edita';
    editBtn.textContent = editMode.active ? ICON_EXIT_EDIT : ICON_ENTER_EDIT;
    editBtn.style.cssText = `
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 15px;
      font-weight: bold;
      font-family: inherit;
      border-radius: 50%;
      border: 1px solid var(--accent-alpha-40);
      background: transparent;
      color: var(--accent);
      cursor: pointer;
      transition: all 0.15s ease;
      flex-shrink: 0;
    `;
    editBtn.addEventListener('click', () => editMode.onToggle());
    topRow.appendChild(editBtn);
  }

  nav.appendChild(topRow);

  // Narrow-screen dropdown menu, full width below the row.
  const menu = document.createElement('div');
  menu.className = 'hidden flex-col gap-1 mt-2';
  for (const def of visibleLinks) {
    menu.appendChild(makeLink(def));
  }
  hamburgerBtn.addEventListener('click', () => {
    menu.classList.toggle('hidden');
    menu.classList.toggle('flex');
  });
  nav.appendChild(menu);

  container.appendChild(nav);
}
